import { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Form, Col, Row, Nav, Tab } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import { debounce } from "lodash";

import SelectStateModal from "../../TreatmentPage/modals/state-modal/SelectStateModal";
import ContactPanelForEditPopups from "../../TreatmentPage/components/ContactPanelForEditPopups";
import { api_without_cancellation } from "../../../api/api";
import { getCaseId, getClientId } from "../../../Utils/helper";
import AutoCompleteSearch from "../../Insurance/AutoCompleteSearch";

// Standard SimpleFirm compact field styling (matches AddNewProvider/etc.)
const inputClass = "form-control height-25 p-0 p-l-5 rounded-0";

function AddEmploymentModal({ handleClose, onFetchEmployment, show, states, isDummy }) {
  // Initialize React Hook Form
  const methods = useForm();
  const { handleSubmit, register, watch, setValue } = methods;

  // Which state picker is open ("employment_state" | "supervisor_state" | "manager_state" | null)
  const [stateModalFor, setStateModalFor] = useState(null);
  // Per-field focus tracking so date inputs can show a text placeholder as their label

  // Watching fields for value changes
  const employmentPhoneValue = watch("employment_phone");
  const employmentFaxValue = watch("employment_fax");
  const supervisorPhoneValue = watch("supervisor_phone");
  const supervisorFaxValue = watch("supervisor_fax");
  const managerPhoneValue = watch("manager_phone");
  const managerFaxValue = watch("manager_fax");

  // Function to format phone numbers
  const formatNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Handle input changes to format phone numbers
  const handleInputChange = (event, fieldName) => {
    const formattedValue = formatNumber(event.target.value);
    setValue(fieldName, formattedValue, { shouldValidate: true });
  };

  useEffect(() => {
    if (show && isDummy) {
      setValue("is_blocked", isDummy);
    }
  }, [show, isDummy]);
  // Clickable trigger that opens SelectStateModal and shows the stored abbreviation.
  // The hidden input keeps the value registered so it submits with the form.
  const renderStateField = (field) => {
    const val = watch(field);
    return (
      <>
        <input type="hidden" {...register(field)} />
        <div
          className="dropdown-button form-control height-25 rounded-0 p-0 p-l-5 d-flex align-items-center"
          role="button"
          tabIndex={0}
          onClick={() => setStateModalFor(field)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setStateModalFor(field);
            }
          }}
          style={{ cursor: "pointer", color: val ? "black" : "var(--primary-25)" }}
        >
          <div className="d-flex align-items-center">
            {val && (
              <svg
                style={{
                  width: "15px",
                  height: "15px",
                  fill: "var(--primary-80)",
                  color: "var(--primary-80)",
                  stroke: "var(--primary-80)",
                }}
                className={`icon icon-state-${val}`}
              >
                <use xlinkHref={`#icon-state-${val}`} />
              </svg>
            )}
            {val || "State"}
          </div>
        </div>
      </>
    );
  };

  // Handle form submission and update data
  const onSubmit = async (data) => {
    try {
      const res = await api_without_cancellation.post(
        `/api/employment/list/${getClientId()}/${getCaseId()}/`,
        data
      );

      if (res.status === 200 || res.status === 201) {
        onFetchEmployment();
        handleClose();
      }
    } catch (error) {
      console.error("Error updating employment:", error);
    }
  };
  const dummy = watch("is_blocked");
  const [searchDisabilityAgencies, setSearchDisabilityAgencies] = useState([]);
  const [companySearchInput, setCompanySearchInput] = useState("");

  const fetchData = async (search) => {
    if (!search || search.length < 3) return;

    try {
      const params = { query: search };

      const response = await api_without_cancellation.get("/api/disability-agencies/search/", {
        params,
      });

      const data = response.data.data;

      setSearchDisabilityAgencies(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const debouncedFetch = useMemo(() => debounce((search) => fetchData(search), 500), []);

  const handleSearchDisabilityAgencies = (event) => {
    const value = event.target.value;

    if (value.length >= 3) {
      debouncedFetch(value);
    } else {
      debouncedFetch.cancel(); // cancel pending request
      setSearchDisabilityAgencies([]);
    }
  };

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const handleSelectDisabilityAgency = (id) => {
    let selectedAgency = searchDisabilityAgencies.filter((agency) => agency.id === id);
    selectedAgency = { ...selectedAgency[0] };
    setCompanySearchInput(selectedAgency?.agency_name || "");
    setValue("agency_dir_id", selectedAgency?.id);
    setValue("disability_lien_name", selectedAgency.agency_name);
    setValue("disability_lien_first_name", selectedAgency?.contact.first_name);
    setValue("disability_lien_last_name", selectedAgency?.contact.last_name);
    setValue("disability_lien_address1", selectedAgency?.contact.address1);
    setValue("disability_lien_address2", selectedAgency?.contact.address2);
    setValue("disability_lien_city", selectedAgency?.contact.city);
    setValue("disability_lien_state", selectedAgency?.contact.state);
    setValue("disability_lien_zip", selectedAgency?.contact.zip);
    setValue("disability_lien_phone_number", formatNumber(selectedAgency?.contact.phone_number));
    setValue("disability_lien_extension", selectedAgency?.contact?.phone_ext || "");
    setValue("disability_lien_fax", formatNumber(selectedAgency?.contact.fax));
    setValue("disability_lien_email", selectedAgency?.contact.email);
    setValue("disability_lien_website", selectedAgency?.contact.website);

    setSearchDisabilityAgencies([]);
  };

  const searchInputRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setSearchDisabilityAgencies([]);
        setCompanySearchInput("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <FormProvider {...methods}>
      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="modal-dialog-centered max-700p custom-insurance-dialog"
        id="employment-edit-modal"
        style={{ opacity: stateModalFor ? 0.5 : 1 }}
      >
        <div style={{ height: "522px" }}>
          <Modal.Header
            style={{
              borderBottom: "0px",
            }}
            className="text-center p-0 bg-primary popup-heading-color justify-content-center"
          >
            <Modal.Title
              className="mx-auto height-25 d-flex align-items-center justify-content-center font-weight-semibold text-uppercase popup-heading-color font-weight-500"
              id="modal_title"
              style={{
                fontSize: "14px",
              }}
            >
              Add Employment
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="p-0">
            <div className="custom-tab">
              <Col md={12} className="d-flex align-items-center justify-content-center">
                <input
                  type="checkbox"
                  id="dummy_checkbox"
                  {...register("is_blocked")}
                  style={{ marginRight: "5px" }}
                />
                <label
                  className="mb-0"
                  style={{
                    textTransform: "uppercase",
                    fontWeight: "600",
                    color: dummy ? "black" : "var(--primary-25)",
                  }}
                >
                  {" "}
                  No Employer on Case
                </label>
              </Col>

              <Tab.Container defaultActiveKey={"employment"}>
                <Nav variant="tabs" className="justify-content-around">
                  <Nav.Link
                    className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-before"
                    eventKey="employment"
                  >
                    Employment
                  </Nav.Link>
                  <Nav.Link
                    className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite"
                    eventKey="supervisor"
                  >
                    Supervisor
                  </Nav.Link>
                  <Nav.Link
                    className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite"
                    eventKey="manager"
                  >
                    Manager
                  </Nav.Link>
                  <Nav.Link
                    className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite"
                    eventKey="disable"
                  >
                    Disability
                  </Nav.Link>

                  <Nav.Link
                    className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-after"
                    eventKey="employeeBottom"
                  >
                    Losses
                  </Nav.Link>
                </Nav>
                <Form
                  className="d-flex flex-column justify-content-between"
                  onSubmit={handleSubmit(onSubmit)}
                  style={{ height: "450px" }}
                  id="editEmploymentContactForm"
                >
                  {/* flex:1 + minHeight:0 + overflowY lets long tabs (Losses) scroll
                      instead of overflowing the fixed 442px form / pushing the footer off */}
                  <fieldset
                    disabled={dummy}
                    style={{
                      border: 0,
                      padding: 0,
                      margin: 0,
                      opacity: dummy ? 0.5 : 1,
                      pointerEvents: dummy ? "none" : "auto",
                    }}
                  >
                    <div
                      className="custom-margin-top"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        scrollbarWidth: "none",
                      }}
                    >
                      <Tab.Content>
                        {/* ============================ EMPLOYMENT ============================ */}
                        <Tab.Pane eventKey="employment">
                          <Row className="mx-0 p-l-5">
                            <ContactPanelForEditPopups
                              id={""}
                              panel_name={"Employment"}
                              className="mb-0"
                              pageName={"Employment"}
                              dynamic_label={"Employer Name"}
                              websiteURL={watch("employment_website")}
                              name={watch("employment_name")}
                              address1={watch("employment_address1")}
                              address2={watch("employment_address2")}
                              email={watch("employment_email")}
                              city={watch("employment_city")}
                              state={watch("employment_state")}
                              zip_code={watch("employment_zip")}
                              phone_number={watch("employment_phone")}
                              fax_number={watch("employment_fax")}
                              buttonData={[]}
                              genrate_doc_address=""
                              ext={watch("employment_extension")}
                            />
                            <Col className="p-0 flex-g-1">
                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-12 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="Employer Name"
                                    className={inputClass}
                                    {...register("employment_name")}
                                  />
                                </div>
                              </div>

                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-6 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="Address 1"
                                    className={inputClass}
                                    {...register("employment_address1")}
                                  />
                                </div>
                                <div className="col-md-6 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="Address 2"
                                    className={inputClass}
                                    {...register("employment_address2")}
                                  />
                                </div>
                              </div>

                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div className="flex-g-1 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="City"
                                    className={inputClass}
                                    {...register("employment_city")}
                                  />
                                </div>
                                <div
                                  style={{
                                    width: "90px",
                                    position: "relative",
                                  }}
                                  className="ps-0 p-r-5 custom-select-new-provider"
                                >
                                  {renderStateField("employment_state")}
                                </div>
                                <div
                                  style={{
                                    width: "110px",
                                  }}
                                  className="ps-0 p-r-5"
                                >
                                  <input
                                    type="text"
                                    placeholder="Zip"
                                    className={inputClass}
                                    {...register("employment_zip")}
                                  />
                                </div>
                              </div>

                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div className="flex-g-1 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="(###) ###-####"
                                    className={inputClass}
                                    {...register("employment_phone")}
                                    value={employmentPhoneValue || ""}
                                    onChange={(e) => handleInputChange(e, "employment_phone")}
                                  />
                                </div>
                                <div
                                  style={{
                                    width: "110px",
                                  }}
                                  className="ps-0 p-r-5"
                                >
                                  <input
                                    type="text"
                                    placeholder="Ext"
                                    className={inputClass}
                                    {...register("employment_extension")}
                                  />
                                </div>
                                <div className="flex-g-1 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="(###) ###-####"
                                    className={inputClass}
                                    {...register("employment_fax")}
                                    value={employmentFaxValue || ""}
                                    onChange={(e) => handleInputChange(e, "employment_fax")}
                                  />
                                </div>
                              </div>

                              <div
                                className="row mx-0 align-items-center custom-margin-bottom 
                            "
                              >
                                <div className="col-md-12 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="Email"
                                    className={inputClass}
                                    {...register("employment_email")}
                                  />
                                </div>
                              </div>

                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-12 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="Website: www.example.com"
                                    className={inputClass}
                                    {...register("employment_website")}
                                  />
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Tab.Pane>

                        {/* ============================ SUPERVISOR ============================ */}
                        <Tab.Pane eventKey="supervisor">
                          <Row className="mx-0 p-l-5 p-r-5">
                            <ContactPanelForEditPopups
                              id={""}
                              panel_name={"Supervisor"}
                              className="mb-0"
                              pageName={"Employment"}
                              websiteURL={watch("supervisor_website")}
                              name={`${watch("supervisor_first_name")} ${watch("supervisor_last_name")}`}
                              address1={watch("supervisor_address1")}
                              address2={watch("supervisor_address2")}
                              email={watch("supervisor_email")}
                              city={watch("supervisor_city")}
                              state={watch("supervisor_state")}
                              zip_code={watch("supervisor_zip")}
                              phone_number={watch("supervisor_phone")}
                              fax_number={watch("supervisor_fax")}
                              buttonData={[]}
                              genrate_doc_address=""
                              ext={watch("supervisor_extension")}
                            />
                            <Col className="flex-g-1 p-0">
                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-6 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="First Name"
                                    className={inputClass}
                                    {...register("supervisor_first_name")}
                                  />
                                </div>
                                <div className="col-md-6 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="Last Name"
                                    className={inputClass}
                                    {...register("supervisor_last_name")}
                                  />
                                </div>
                              </div>

                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-6 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="Address 1"
                                    className={inputClass}
                                    {...register("supervisor_address1")}
                                  />
                                </div>

                                <div className="col-md-6 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="Address 2"
                                    className={inputClass}
                                    {...register("supervisor_address2")}
                                  />
                                </div>
                              </div>

                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div className="flex-g-1 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="City"
                                    className={inputClass}
                                    {...register("supervisor_city")}
                                  />
                                </div>
                                <div
                                  style={{
                                    width: "90px",
                                    position: "relative",
                                  }}
                                  className="ps-0 p-r-5 custom-select-new-provider"
                                >
                                  {renderStateField("supervisor_state")}
                                </div>
                                <div
                                  style={{
                                    width: "110px",
                                  }}
                                  className="ps-0 pe-0"
                                >
                                  <input
                                    type="text"
                                    placeholder="Zip"
                                    className={inputClass}
                                    {...register("supervisor_zip")}
                                  />
                                </div>
                              </div>

                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div className="flex-g-1 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="(###) ###-####"
                                    className={inputClass}
                                    {...register("supervisor_phone")}
                                    value={supervisorPhoneValue || ""}
                                    onChange={(e) => handleInputChange(e, "supervisor_phone")}
                                  />
                                </div>
                                <div
                                  style={{
                                    width: "110px",
                                  }}
                                  className="ps-0 p-r-5
                              "
                                >
                                  <input
                                    type="text"
                                    placeholder="Ext"
                                    className={inputClass}
                                    {...register("supervisor_extension")}
                                  />
                                </div>
                                <div className="flex-g-1 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="(###) ###-####"
                                    className={inputClass}
                                    {...register("supervisor_fax")}
                                    value={supervisorFaxValue || ""}
                                    onChange={(e) => handleInputChange(e, "supervisor_fax")}
                                  />
                                </div>
                              </div>

                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-12 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="Email"
                                    className={inputClass}
                                    {...register("supervisor_email")}
                                  />
                                </div>
                              </div>

                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-12 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="Website: www.example.com"
                                    className={inputClass}
                                    {...register("supervisor_website")}
                                  />
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Tab.Pane>

                        {/* ============================ MANAGER ============================ */}
                        <Tab.Pane eventKey="manager">
                          <Row className="mx-0 p-l-5 p-r-5">
                            <ContactPanelForEditPopups
                              id={""}
                              panel_name={"Manager"}
                              className="mb-0"
                              pageName={"Employment"}
                              websiteURL={watch("manager_website")}
                              name={`${watch("manager_first_name")} ${watch("manager_last_name")}`}
                              address1={watch("manager_address1")}
                              address2={watch("manager_address2")}
                              email={watch("manager_email")}
                              city={watch("manager_city")}
                              state={watch("manager_state")}
                              zip_code={watch("manager_zip")}
                              phone_number={watch("manager_phone")}
                              fax_number={watch("manager_fax")}
                              buttonData={[]}
                              genrate_doc_address=""
                              ext={watch("manager_extension")}
                            />
                            <Col className="flex-g-1 p-0">
                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-6 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="First Name"
                                    className={inputClass}
                                    {...register("manager_first_name")}
                                  />
                                </div>
                                <div className="col-md-6 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="Last Name"
                                    className={inputClass}
                                    {...register("manager_last_name")}
                                  />
                                </div>
                              </div>

                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-6 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="Address 1"
                                    className={inputClass}
                                    {...register("manager_address1")}
                                  />
                                </div>
                                <div className="col-md-6 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="Address 2"
                                    className={inputClass}
                                    {...register("manager_address2")}
                                  />
                                </div>
                              </div>

                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div className="flex-g-1 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="City"
                                    className={inputClass}
                                    {...register("manager_city")}
                                  />
                                </div>
                                <div
                                  style={{
                                    width: "90px",
                                    position: "relative",
                                  }}
                                  className="ps-0 p-r-5 custom-select-new-provider"
                                >
                                  {renderStateField("manager_state")}
                                </div>
                                <div
                                  style={{
                                    width: "110px",
                                  }}
                                  className="ps-0 pe-0"
                                >
                                  <input
                                    type="text"
                                    placeholder="Zip"
                                    className={inputClass}
                                    {...register("manager_zip")}
                                  />
                                </div>
                              </div>

                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div className="flex-g-1 ps-0 p-r-5">
                                  <input
                                    type="text"
                                    placeholder="(###) ###-####"
                                    className={inputClass}
                                    {...register("manager_phone")}
                                    value={managerPhoneValue || ""}
                                    onChange={(e) => handleInputChange(e, "manager_phone")}
                                  />
                                </div>
                                <div
                                  style={{
                                    width: "110px",
                                  }}
                                  className="ps-0 p-r-5"
                                >
                                  <input
                                    type="text"
                                    placeholder="Ext"
                                    className={inputClass}
                                    {...register("manager_extension")}
                                  />
                                </div>
                                <div className="flex-g-1 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="(###) ###-####"
                                    className={inputClass}
                                    {...register("manager_fax")}
                                    value={managerFaxValue || ""}
                                    onChange={(e) => handleInputChange(e, "manager_fax")}
                                  />
                                </div>
                              </div>

                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-12 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="Email"
                                    className={inputClass}
                                    {...register("manager_email")}
                                  />
                                </div>
                              </div>

                              <div className="row mx-0 align-items-center custom-margin-bottom">
                                <div className="col-md-12 ps-0 pe-0">
                                  <input
                                    type="text"
                                    placeholder="Website: www.example.com"
                                    className={inputClass}
                                    {...register("manager_website")}
                                  />
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Tab.Pane>

                        {/* ============================ DISABILITY INFORMATION ============================ */}
                        <Tab.Pane eventKey="disable">
                          <>
                            <Row className="mx-0">
                              <Col md={12} className="p-l-5 p-r-5">
                                <Form.Control
                                  type="text"
                                  value={companySearchInput}
                                  name="search_filter_insurance_input"
                                  placeholder="Type disability agency name to search directory then click an entry"
                                  className="custom-margin-bottom height-25 p-0 p-l-5 rounded-0"
                                  onChange={(e) => {
                                    setCompanySearchInput(e.target.value);
                                    handleSearchDisabilityAgencies(e);
                                  }}
                                  ref={searchInputRef}
                                />
                                {companySearchInput && (
                                  <span
                                    onClick={() => {
                                      setCompanySearchInput("");
                                      handleSearchDisabilityAgencies({ target: { value: "" } });
                                      setValue("agency_dir_id", "");
                                      setValue("disability_lien_name", "");
                                      setValue("disability_lien_first_name", "");
                                      setValue("disability_lien_last_name", "");
                                      setValue("disability_lien_address1", "");
                                      setValue("disability_lien_address2", "");
                                      setValue("disability_lien_city", "");
                                      setValue("disability_lien_state", "");
                                      setValue("disability_lien_zip", "");
                                      setValue("disability_lien_phone_number", "");
                                      setValue("disability_lien_extension", "");
                                      setValue("disability_lien_fax", "");
                                      setValue("disability_lien_email", "");
                                      setValue("disability_lien_website", "");
                                    }}
                                    style={{
                                      position: "absolute",
                                      right: "15px",
                                      top: "5px",
                                      cursor: "pointer",
                                      fontSize: "14px",
                                      color: "black",
                                      lineHeight: 1,
                                      userSelect: "none",
                                    }}
                                  >
                                    ✕
                                  </span>
                                )}
                                {searchDisabilityAgencies.length > 0 && (
                                  <AutoCompleteSearch
                                    searchedInsurances={searchDisabilityAgencies}
                                    selectedInsurance={handleSelectDisabilityAgency}
                                    isAgency={true}
                                  />
                                )}
                              </Col>
                            </Row>
                            <Row className="mx-0 p-l-5">
                              <ContactPanelForEditPopups
                                panel_name={"Disability Agency"}
                                className="m-b-5"
                                dynamic_label={"Agency Name"}
                                name={watch("disability_lien_name")}
                                address1={watch("disability_lien_address1")}
                                address2={watch("disability_lien_address2")}
                                email={watch("disability_lien_email")}
                                phone_number={watch("disability_lien_phone_number")}
                                city={watch("disability_lien_city")}
                                state={watch("disability_lien_state")}
                                zip_code={watch("disability_lien_zip")}
                                fax_number={watch("disability_lien_fax")}
                                ext={watch("disability_lien_extension")}
                                websiteURL={watch("disability_lien_website")}
                                buttonData={[]}
                                pageName="Work"
                              />

                              <Col md={"auto"} className="p-0 d-flex-1">
                                <div className="d-flex-1 p-l-0 p-r-5 custom-margin-bottom">
                                  <Form.Control
                                    type="text"
                                    className="height-25 p-0 p-l-5 rounded-0"
                                    placeholder="Name"
                                    {...register("disability_lien_name")}
                                  />
                                </div>
                                <Row className="align-items-center custom-margin-bottom mx-0">
                                  <div className="d-flex-1 p-r-5 p-l-0">
                                    <Form.Control
                                      type="text"
                                      className="height-25 p-0 p-l-5 rounded-0"
                                      placeholder="First Name"
                                      {...register("disability_lien_first_name")}
                                    />
                                  </div>
                                  <div className="d-flex-1 p-l-0 p-r-5">
                                    <Form.Control
                                      type="text"
                                      className="height-25 p-0 p-l-5 rounded-0"
                                      placeholder="Last Name"
                                      {...register("disability_lien_last_name")}
                                    />
                                  </div>
                                </Row>
                                <Row className="align-items-center custom-margin-bottom mx-0">
                                  <div className="d-flex-1 p-r-5 p-l-0">
                                    <Form.Control
                                      type="text"
                                      className="height-25 p-0 p-l-5 rounded-0"
                                      placeholder="Address 1"
                                      {...register("disability_lien_address1")}
                                    />
                                  </div>
                                  <div className="d-flex-1 p-l-0 p-r-5">
                                    <Form.Control
                                      type="text"
                                      className="height-25 p-0 p-l-5 rounded-0"
                                      placeholder="Address 2"
                                      {...register("disability_lien_address2")}
                                    />
                                  </div>
                                </Row>
                                <Row className="d-flex align-items-center custom-margin-bottom mx-0">
                                  <div className="d-flex-1 p-l-0 p-r-5">
                                    <Form.Control
                                      type="text"
                                      placeholder="City"
                                      className="form-control height-25 p-0 p-l-5 rounded-0"
                                      {...register("disability_lien_city")}
                                    />
                                  </div>
                                  <div
                                    style={{
                                      width: "90px",
                                      maxWidth: "90px",
                                    }}
                                    className="p-l-0 position-relative height-25 p-r-5 custom-select-new-provider"
                                  >
                                    {renderStateField("disability_lien_state")}
                                  </div>
                                  <div
                                    style={{
                                      width: "110px",
                                      maxWidth: "110px",
                                    }}
                                    className="p-r-5"
                                  >
                                    <Form.Control
                                      type="text"
                                      placeholder="Zip"
                                      className="height-25 p-0 p-l-5 rounded-0"
                                      {...register("disability_lien_zip")}
                                    />
                                  </div>
                                </Row>
                                <Row className="align-items-center custom-margin-bottom mx-0">
                                  <div className="d-flex-1 p-r-5 p-l-0">
                                    <Form.Control
                                      type="text"
                                      placeholder="(###) ###-####"
                                      className="height-25 p-0 p-l-5 rounded-0"
                                      {...register("disability_lien_phone_number")}
                                      onChange={(e) =>
                                        handleInputChange(e, "disability_lien_phone_number")
                                      }
                                    />
                                  </div>
                                  <div
                                    style={{
                                      width: "100px",
                                      maxWidth: "100px",
                                    }}
                                    className="p-l-0 p-r-5"
                                  >
                                    <Form.Control
                                      type="text"
                                      placeholder="Extension"
                                      className="form-control height-25 p-0 p-l-5 rounded-0"
                                      {...register("disability_lien_extension")}
                                    />
                                  </div>
                                  <div className="d-flex-1 p-l-0 p-r-5">
                                    <Form.Control
                                      type="text"
                                      placeholder="(###) ###-####"
                                      className="height-25 p-0 p-l-5 rounded-0"
                                      {...register("disability_lien_fax")}
                                      onChange={(e) => handleInputChange(e, "disability_lien_fax")}
                                    />
                                  </div>
                                </Row>
                                <div className="d-flex-1 p-l-0 p-r-5 custom-margin-bottom">
                                  <Form.Control
                                    type="text"
                                    className="height-25 p-0 p-l-5 rounded-0"
                                    placeholder="Email"
                                    {...register("disability_lien_email")}
                                  />
                                </div>
                                <div className="d-flex-1 p-l-0 p-r-5 custom-margin-bottom">
                                  <Form.Control
                                    type="text"
                                    className="height-25 p-0 p-l-5 rounded-0"
                                    placeholder="Website"
                                    {...register("disability_lien_website")}
                                  />
                                </div>
                              </Col>
                            </Row>
                            <Row
                              style={{ paddingLeft: "5px", paddingRight: "5px" }}
                              className="mx-0"
                            >
                              <Col md={12} className="p-0">
                                {/* Account # | Lien */}
                                <div className="row mx-0 align-items-center custom-margin-bottom">
                                  <div
                                    className="ps-0 p-r-5"
                                    style={{
                                      width: "150px",
                                      color: "var(--primary-25)",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Account #:
                                  </div>
                                  <div
                                    style={{
                                      maxWidth: "195px",
                                      width: "195px",
                                    }}
                                    className="height-25 p-l-0 pe-0"
                                  >
                                    <input
                                      type="text"
                                      className="form-control height-25 p-0 p-l-5 rounded-0"
                                      style={{ color: "black" }}
                                      {...register("account_number")}
                                    />
                                  </div>
                                  <div
                                    className="p-l-5 p-r-5"
                                    style={{
                                      width: "150px",
                                      color: "var(--primary-25)",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Lien:
                                  </div>
                                  <div
                                    style={{
                                      maxWidth: "195px",
                                    }}
                                    className="height-25 p-l-0 pe-0 position-relative"
                                  >
                                    <span
                                      style={{
                                        position: "absolute",
                                        left: "8px",
                                        lineHeight: "25px",
                                        pointerEvents: "none",
                                        zIndex: 1,
                                      }}
                                    >
                                      $
                                    </span>
                                    <input
                                      type="text"
                                      className="form-control height-25 rounded-0"
                                      style={{ color: "black", paddingLeft: "18px" }}
                                      {...register("original_lien")}
                                    />
                                  </div>
                                </div>

                                {/* Final Lien | Date Verified */}
                                <div className="row mx-0 align-items-center custom-margin-bottom">
                                  <div
                                    className="ps-0 p-r-5"
                                    style={{
                                      width: "150px",
                                      color: "var(--primary-25)",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Final Lien:
                                  </div>
                                  <div
                                    style={{
                                      maxWidth: "195px",
                                    }}
                                    className="flex-g-1 height-25 p-l-0 pe-0 position-relative"
                                  >
                                    <span
                                      style={{
                                        position: "absolute",
                                        left: "8px",
                                        lineHeight: "25px",
                                        pointerEvents: "none",
                                        zIndex: 1,
                                      }}
                                    >
                                      $
                                    </span>
                                    <input
                                      type="text"
                                      className="form-control height-25 rounded-0"
                                      style={{ color: "black", paddingLeft: "18px" }}
                                      {...register("final_lien")}
                                    />
                                  </div>
                                  <div
                                    className="p-l-5 p-r-5"
                                    style={{
                                      width: "150px",
                                      color: "var(--primary-25)",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Date Verified:
                                  </div>
                                  <div className="flex-g-1 height-25 p-l-0 pe-0">
                                    <input
                                      type="date"
                                      className="form-control height-25 p-0 p-l-5 rounded-0"
                                      style={{ color: "black" }}
                                      {...register("date_verified")}
                                    />
                                  </div>
                                </div>

                                {/* Lien Paid Date | Lien Paid Amount */}
                                <div className="row mx-0 align-items-center custom-margin-bottom">
                                  <div
                                    className="ps-0 p-r-5"
                                    style={{
                                      width: "150px",
                                      color: "var(--primary-25)",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Lien Paid Date:
                                  </div>
                                  <div
                                    style={{
                                      width: "195px",
                                      maxWidth: "195px",
                                    }}
                                    className="flex-g-1 height-25 p-l-0 pe-0"
                                  >
                                    <input
                                      type="date"
                                      className="form-control height-25 p-0 p-l-5 rounded-0"
                                      style={{ color: "black" }}
                                      {...register("lien_paid_date")}
                                    />
                                  </div>
                                  <div
                                    className="p-l-5 p-r-5"
                                    style={{
                                      width: "150px",
                                      color: "var(--primary-25)",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Lien Paid Amount:
                                  </div>
                                  <div
                                    style={{
                                      maxWidth: "195px",
                                    }}
                                    className="flex-g-1 height-25 p-l-0 pe-0 position-relative"
                                  >
                                    <span
                                      style={{
                                        position: "absolute",
                                        left: "8px",
                                        lineHeight: "25px",
                                        pointerEvents: "none",
                                        zIndex: 1,
                                      }}
                                    >
                                      $
                                    </span>
                                    <input
                                      type="text"
                                      className="form-control height-25 rounded-0"
                                      style={{ color: "black", paddingLeft: "18px" }}
                                      {...register("lien_paid_amount")}
                                    />
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </>
                        </Tab.Pane>

                        {/* ============================ LOSSES ============================ */}
                        <Tab.Pane eventKey="employeeBottom">
                          <Row style={{ paddingLeft: "5px", paddingRight: "5px" }} className="mx-0">
                            <Col md={12} className="p-0">
                              {/* Rate of Pay */}
                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div
                                  className="ps-0 p-r-5"
                                  style={{
                                    width: "150px",
                                    color: "var(--primary-25)",
                                    fontWeight: "600",
                                  }}
                                >
                                  Total Lost Wages:
                                </div>
                                <div
                                  style={{
                                    maxWidth: "195px",
                                  }}
                                  className="flex-g-1 height-25 p-l-0 pe-0 position-relative"
                                >
                                  <span
                                    style={{
                                      position: "absolute",
                                      left: "8px",
                                      lineHeight: "25px",
                                      pointerEvents: "none",
                                      zIndex: 1,
                                    }}
                                  >
                                    $
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control height-25  rounded-0"
                                    style={{ color: "black", paddingLeft: "18px" }}
                                    {...register("total_wages_lost")}
                                  />
                                </div>
                                <div
                                  className="p-l-5 p-r-5"
                                  style={{
                                    width: "150px",
                                    color: "var(--primary-25)",
                                    fontWeight: "600",
                                  }}
                                >
                                  Rate of Pay:
                                </div>
                                <div
                                  style={{
                                    maxWidth: "195px",
                                  }}
                                  className="flex-g-1 height-25 p-l-0 pe-0 position-relative"
                                >
                                  <span
                                    style={{
                                      position: "absolute",
                                      left: "8px",
                                      lineHeight: "25px",
                                      pointerEvents: "none",
                                      zIndex: 1,
                                    }}
                                  >
                                    $
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control height-25 rounded-0"
                                    style={{ color: "black", paddingLeft: "18px" }}
                                    {...register("rate_of_pay")}
                                  />
                                </div>
                              </div>

                              {/* Disability Paid | Sick Time Used */}
                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div
                                  className="ps-0 p-r-5"
                                  style={{
                                    width: "150px",
                                    color: "var(--primary-25)",
                                    fontWeight: "600",
                                  }}
                                >
                                  Disability Paid:
                                </div>
                                <div
                                  style={{
                                    maxWidth: "195px",
                                  }}
                                  className="flex-g-1 height-25 p-l-0 pe-0 position-relative"
                                >
                                  <span
                                    style={{
                                      position: "absolute",
                                      left: "8px",
                                      lineHeight: "25px",
                                      pointerEvents: "none",
                                      zIndex: 1,
                                    }}
                                  >
                                    $
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control height-25 rounded-0"
                                    style={{ color: "black", paddingLeft: "18px" }}
                                    {...register("disability_paid")}
                                  />
                                </div>
                                <div
                                  className="p-l-5 p-r-5"
                                  style={{
                                    width: "150px",
                                    color: "var(--primary-25)",
                                    fontWeight: "600",
                                  }}
                                >
                                  Sick Time Used:
                                </div>
                                <div className="flex-g-1 height-25 p-l-0 pe-0">
                                  <input
                                    type="text"
                                    className="form-control height-25 p-0 p-l-5 rounded-0"
                                    style={{ color: "black" }}
                                    {...register("sick_time_used")}
                                  />
                                </div>
                              </div>

                              {/* Total Time Missed | Date Terminated */}
                              <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                                <div
                                  className="ps-0 p-r-5"
                                  style={{
                                    width: "150px",
                                    color: "var(--primary-25)",
                                    fontWeight: "600",
                                  }}
                                >
                                  Total Time Missed:
                                </div>
                                <div
                                  className="flex-g-1 height-25 p-l-0 pe-0"
                                  style={{
                                    maxWidth: "195px",
                                  }}
                                >
                                  <input
                                    type="text"
                                    className="form-control height-25 p-0 p-l-5 rounded-0"
                                    style={{ color: "black" }}
                                    {...register("total_time_missed")}
                                  />
                                </div>
                                <div
                                  className="p-l-5 p-r-5"
                                  style={{
                                    width: "150px",
                                    color: "var(--primary-25)",
                                    fontWeight: "600",
                                  }}
                                >
                                  Date Terminated:
                                </div>
                                <div className="flex-g-1 height-25 p-l-0 pe-0">
                                  <input
                                    type="date"
                                    className="form-control height-25 p-0 p-l-5 rounded-0"
                                    style={{ color: "black" }}
                                    {...register("date_terminated")}
                                  />
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Tab.Pane>
                      </Tab.Content>
                    </div>
                  </fieldset>
                  <div
                    className="p-0 mt-0 d-flex align-items-center justify-content-between padding-outside-btn-new-provider"
                    style={{ borderTop: "none" }}
                  >
                    <Button
                      className="button-padding-footer-new-provider height-25 d-flex align-items-center justify-content-center "
                      variant="secondary"
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>

                    <Button
                      className="button-padding-footer-new-provider height-25  d-flex align-items-center justify-content-center "
                      variant="success"
                      type="submit"
                    >
                      Save and Close
                    </Button>
                  </div>
                </Form>
              </Tab.Container>
            </div>
          </Modal.Body>
        </div>
      </Modal>

      {stateModalFor && (
        <SelectStateModal
          show={!!stateModalFor}
          handleClose={() => setStateModalFor(null)}
          onChange={(state) => {
            setValue(stateModalFor, state.StateAbr, { shouldValidate: true });
            setStateModalFor(null);
          }}
          statesData={states}
        />
      )}
    </FormProvider>
  );
}

export default AddEmploymentModal;
